import { ReactElement, useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import * as RadixToast from '@radix-ui/react-toast'

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 50000

type ToastVariant = 'info' | 'success' | 'error'

export type ToasterToast = RadixToast.ToastProps & {
    id: string
    variant: ToastVariant
    title?: React.ReactNode
    description?: React.ReactNode
    action?: ReactElement<typeof RadixToast.Action>
    onClose: () => void
}

const actionTypes = {
    ADD_TOAST: 'ADD_TOAST',
    UPDATE_TOAST: 'UPDATE_TOAST',
    DISMISS_TOAST: 'DISMISS_TOAST',
    REMOVE_TOAST: 'REMOVE_TOAST',
} as const

let count = 0

function genId() {
    count = (count + 1) % Number.MAX_SAFE_INTEGER
    return count.toString()
}

type ActionType = typeof actionTypes

type Action =
    | {
          type: ActionType['ADD_TOAST']
          toast: ToasterToast
      }
    | {
          type: ActionType['UPDATE_TOAST']
          toast: Partial<ToasterToast>
      }
    | {
          type: ActionType['DISMISS_TOAST']
          toastId?: ToasterToast['id']
      }
    | {
          type: ActionType['REMOVE_TOAST']
          toastId?: ToasterToast['id']
      }

interface State {
    toasts: ToasterToast[]
}

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
    if (toastTimeouts.has(toastId)) {
        return
    }

    const timeout = setTimeout(() => {
        toastTimeouts.delete(toastId)
        dispatch({
            type: 'REMOVE_TOAST',
            toastId: toastId,
        })
    }, TOAST_REMOVE_DELAY)

    toastTimeouts.set(toastId, timeout)
}

export const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'ADD_TOAST':
            return {
                ...state,
                toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
            }

        case 'UPDATE_TOAST':
            return {
                ...state,
                toasts: state.toasts.map((t) =>
                    t.id === action.toast.id ? { ...t, ...action.toast } : t
                ),
            }

        case 'DISMISS_TOAST': {
            const { toastId } = action

            // ! Side effects ! - This could be extracted into a dismissToast() action,
            // but I'll keep it here for simplicity
            if (toastId) {
                addToRemoveQueue(toastId)
            } else {
                state.toasts.forEach((toast) => {
                    addToRemoveQueue(toast.id)
                })
            }

            return {
                ...state,
                toasts: state.toasts.map((t) =>
                    t.id === toastId || toastId === undefined
                        ? {
                              ...t,
                              open: false,
                          }
                        : t
                ),
            }
        }
        case 'REMOVE_TOAST':
            if (action.toastId === undefined) {
                return {
                    ...state,
                    toasts: [],
                }
            }
            return {
                ...state,
                toasts: state.toasts.filter((t) => t.id !== action.toastId),
            }
    }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
    memoryState = reducer(memoryState, action)
    listeners.forEach((listener) => {
        listener(memoryState)
    })
}

export const showToast = (variant: ToastVariant) => (description: string) => {
    const id = genId()

    const dismiss = () =>
        dispatch({
            type: 'DISMISS_TOAST',
            toastId: id,
        })

    dispatch({
        type: 'ADD_TOAST',
        toast: {
            id,
            open: true,
            variant,
            description,
            onClose: () => dismiss(),
            onOpenChange: (open) => {
                if (!open && variant !== 'info') dismiss()
            },
        },
    })

    return {
        dismiss,
    }
}

export const showToastInfo = showToast('info')
export const showToastSuccess = showToast('success')
export const showToastError = (error: unknown) => {
    if (typeof error === 'string') {
        return showToast('error')(error)
    }

    const typedError = error as AxiosError<{ error: string }>
    const message = typedError.response?.data?.error || 'Error making request'
    return showToast('error')(message)
}

export const useToast = () => {
    const [state, setState] = useState<State>(memoryState)

    useEffect(() => {
        listeners.push(setState)
        return () => {
            const index = listeners.indexOf(setState)
            if (index > -1) {
                listeners.splice(index, 1)
            }
        }
    }, [state])

    return {
        ...state,
    }
}
